import { getGetFrontFilePathNomeFileQueryOptions } from '@framework/api-client/api/inquiries-compile/file-controller/file-controller.ts';
import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { useWidgetEnabled } from '@framework/engine/schema/widgets/hooks/useWidgetEnabled.ts';
import { useWidgetVisibility } from '@framework/engine/schema/widgets/hooks/useWidgetVisibility.ts';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { getIconFromType } from '@framework/engine/schema/widgets/repository/advanced/buttons/icons/Icons.tsx';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { InputMarginContainer } from '@framework/engine/ui/row/InputMarginContainer.tsx';
import { AlignRow } from '@framework/ui/components/row/AlignRow.tsx';
import { Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthDownload } from '../../../utils/download/useAuthDownload.ts';
import { type IDownloadWidget } from './IDownloadWidget.ts';

export interface IDownloadWidgetComponentProps
  extends IWidgetComponentProps<IDownloadWidget> {}

/**
 * Widget per il download di un file
 */
export function DownloadWidgetComponent(props: IDownloadWidgetComponentProps) {
  const { widget } = props;
  const { t } = useTranslation();
  const options = getWidgetProps(props);

  const visible = useWidgetVisibility(props.widget, options);
  const enabled = useWidgetEnabled(widget, options);
  // Estraggo le informazioni necessarie per il download del file
  const [path, fileName] = widget.filePath?.split('/') ?? [];

  // Hook di antd necessario per gestire i messaggi
  // di loading durante le azioni di richiamo servizio
  const [messageApi, contextHolder] = message.useMessage();

  const { download, isFetching } = useAuthDownload(
    getGetFrontFilePathNomeFileQueryOptions,
    [path, fileName],
    {
      onError: e => {
        if (e.response?.status === 404) {
          message.error(
            t('download-widget.file-not-found', 'File non trovato'),
          );
          return;
        }

        // biome-ignore format: <tbd>
        message.error(t("download-widget.error", 'Si è verificato un problema durante il download del file. Riprovare.'));
      },
    },
  );

  if (!visible) return null;
  if (props.readonly) return null;

  return (
    <>
      {contextHolder}
      <InputMarginContainer>
        <AlignRow justify={widget.layout?.buttonAlign ?? 'flex-end'}>
          <Button
            size="large"
            block
            type={widget.layout?.buttonType ?? 'primary'}
            icon={getIconFromType(widget.layout?.iconType)}
            disabled={enabled === false}
            loading={isFetching}
            onClick={download}
          >
            <RichTextAdvancedRender inline text={widget.label} />
          </Button>
        </AlignRow>
      </InputMarginContainer>
    </>
  );
}
